// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import { StatusBarContainer, ToastStyle } from 'components/treactui-template/organisms/toast/ToastStyle';
import { HubContext, HubContextType } from 'components/common/emitter/HubContext';

import React, { useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer, cssTransition } from 'react-toastify/unstyled';

const Fade = cssTransition({
  enter: 'sw91-toast-fade-in',
  exit: 'sw91-toast-fade-out',
});
import I18nextProvider from 'utilities/translation/I18nextProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SetDispatch, eventEmitter } from 'hub/Connector';
import { reactPlugin } from 'utilities/appInsights/AppInsights';
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import ErrorBound from 'components/common/ErrorBound';
import ContractFetcher from 'components/ContractFetcher';
import ApplicationRouter from 'layout/ApplicationRouter';
import { ThirdPartyLicensesDialog } from 'components/thirdPartyLicenses/ThirdPartyLicensesDialog';
import { useAppDispatch } from 'store/hooks';
import { DataAgreementDialog } from 'components/settings/DataAgreementDialog';
import { InitialDataAgreementDialog } from 'components/settings/InitialDataAgreementDialog';
import TelemetryFetcher from 'components/TelemetryFetcher';
import { ExportStatusProvider } from 'components/projectSelection/ExportStatusProvider';
import ExportProjectDialog from 'components/projectSelection/ExportProjectDialog';

const queryClient = new QueryClient();

export default function App() {
  const { appBaseUrl } = useAuthContext();
  const dispatch = useAppDispatch();

  useEffect(() => {
    SetDispatch(dispatch);
  }, [dispatch]);

  const hubContextValue = useMemo<HubContextType>(() => ({ events: eventEmitter }), []);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <AppInsightsErrorBoundary onError={ErrorBound} appInsights={reactPlugin}>
        <I18nextProvider appBaseUrl={appBaseUrl}>
          <HubContext.Provider value={hubContextValue}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter basename={`${appBaseUrl}`}>
                <ExportStatusProvider>
                  <ToastStyle />
                  <StatusBarContainer>
                    <ToastContainer
                      position='top-right'
                      autoClose={8000}
                      hideProgressBar={true}
                      closeButton={false}
                      closeOnClick={false}
                      transition={Fade}
                      style={{
                        zIndex: '1000000',
                        position: 'absolute',
                        right: 0,
                      }}
                      pauseOnHover={false}
                      newestOnTop
                      rtl={false}
                      pauseOnFocusLoss={false}
                      draggable={false}
                    />
                  </StatusBarContainer>
                  <ContractFetcher />
                  <ThirdPartyLicensesDialog />
                  <DataAgreementDialog />
                  <InitialDataAgreementDialog />
                  <ApplicationRouter />
                  <ExportProjectDialog />
                  <TelemetryFetcher />
                </ExportStatusProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </HubContext.Provider>
        </I18nextProvider>
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  );
}
